import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { itemCount } = useCart();
  const { user, login, logout } = useAuth();
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Inicio' },
    { href: '/menu', label: 'Menú' },
    { href: '/subscriptions', label: 'Planes' },
    { href: '/faq', label: 'Ayuda' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-stone-200/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Lunch & Roll"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-emerald-700'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
                data-testid={link.href === '/menu' ? 'menu-link' : link.href === '/subscriptions' ? 'subscriptions-link' : `nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Cart */}
            <Link to="/cart" className="relative" data-testid="cart-button">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ShoppingCart className="w-5 h-5" />
              </Button>
              {itemCount > 0 && (
                <Badge className="absolute -top-1 -right-1 w-5 h-5 p-0 flex items-center justify-center bg-orange-500 text-white text-xs border-0">
                  {itemCount}
                </Badge>
              )}
            </Link>

            {/* User */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full overflow-hidden" data-testid="user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-stone-900">{user.name}</p>
                    <p className="text-sm text-stone-500">{user.email}</p>
                    {user.role === 'master_admin' && (
                      <Badge className="mt-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 text-xs">
                        Master Admin
                      </Badge>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" data-testid="dropdown-dashboard">
                      Mi Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer" data-testid="dropdown-settings">
                      Configuración
                    </Link>
                  </DropdownMenuItem>
                  {['admin', 'master_admin'].includes(user.role) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin-dashboard" className="cursor-pointer font-medium text-emerald-700" data-testid="dropdown-admin">
                          Panel Admin
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/operations" className="cursor-pointer font-medium text-orange-600" data-testid="dropdown-operations">
                          Centro de Operaciones
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer" data-testid="dropdown-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={login}
                className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-6 hidden md:flex"
                data-testid="login-btn"
              >
                Iniciar Sesión
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-stone-200">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`block py-2 font-medium ${
                  isActive(link.href) ? 'text-emerald-700' : 'text-stone-600'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {user && ['admin', 'master_admin'].includes(user.role) && (
              <>
                <div className="border-t border-stone-200 pt-3 mt-3">
                  <p className="text-xs text-stone-400 uppercase font-medium mb-2">Administración</p>
                  <Link
                    to="/admin-dashboard"
                    className="block py-2 font-medium text-emerald-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Panel Admin
                  </Link>
                  <Link
                    to="/operations"
                    className="block py-2 font-medium text-orange-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Centro de Operaciones
                  </Link>
                </div>
              </>
            )}
            {!user && (
              <Button
                onClick={() => { login(); setMobileMenuOpen(false); }}
                className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full mt-4"
              >
                Iniciar Sesión
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
